import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import * as os from "os"
// Note: Do NOT import logger here - it creates a circular dependency
// utils.ts -> api-utils.ts -> auth.ts -> blockchain -> atomicassets-minting-service -> api-utils.ts

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim()
}

/**
 * Generate a unique slug by appending a numeric suffix if needed.
 *
 * Consolidates the common pattern of:
 * ```typescript
 * const baseSlug = generateSlug(title);
 * let slug = baseSlug;
 * let suffix = 1;
 * while (await checkExists(slug)) {
 *   slug = `${baseSlug}-${suffix}`;
 *   suffix++;
 * }
 * ```
 *
 * @param baseText - The text to generate a slug from
 * @param checkExists - Async function that returns true if the slug already exists
 * @param options - Optional configuration
 * @returns A unique slug
 *
 * @example
 * ```typescript
 * const slug = await generateUniqueSlug(
 *   "My Campaign",
 *   async (slug) => !!(await prisma.campaign.findUnique({ where: { slug } }))
 * );
 * // Returns "my-campaign" or "my-campaign-1", "my-campaign-2", etc.
 * ```
 */
export async function generateUniqueSlug(
  baseText: string,
  checkExists: (slug: string) => Promise<boolean>,
  options: {
    /** Maximum number of attempts before giving up (default: 100) */
    maxAttempts?: number;
    /** Separator between base slug and suffix (default: "-") */
    separator?: string;
  } = {}
): Promise<string> {
  const { maxAttempts = 100, separator = '-' } = options;
  const baseSlug = generateSlug(baseText);

  // Handle empty slug case
  if (!baseSlug) {
    const timestamp = Date.now().toString(36);
    return `item${separator}${timestamp}`;
  }

  let slug = baseSlug;
  let suffix = 1;

  while (await checkExists(slug)) {
    if (suffix >= maxAttempts) {
      // Append timestamp to ensure uniqueness
      const timestamp = Date.now().toString(36);
      return `${baseSlug}${separator}${timestamp}`;
    }
    slug = `${baseSlug}${separator}${suffix}`;
    suffix++;
  }

  return slug;
}

/**
 * Sanitize a name for use as a schema/collection name.
 * Similar to generateSlug but with different character rules.
 *
 * @param name - The name to sanitize
 * @returns A sanitized name safe for schema/collection use
 */
export function sanitizeSchemaName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric
    .slice(0, 12); // WAX schema names have length limits
}

/**
 * Generate a unique schema name by appending a numeric suffix if needed.
 *
 * @param baseName - The base name to use
 * @param checkExists - Async function that returns true if the name already exists
 * @returns A unique schema name
 */
export async function generateUniqueSchemaName(
  baseName: string,
  checkExists: (name: string) => Promise<boolean>
): Promise<string> {
  const sanitized = sanitizeSchemaName(baseName);

  if (!sanitized) {
    const timestamp = Date.now().toString(36).slice(-6);
    return `schema${timestamp}`;
  }

  let name = sanitized;
  let suffix = 1;

  while (await checkExists(name)) {
    if (suffix >= 100) {
      const timestamp = Date.now().toString(36).slice(-4);
      return `${sanitized.slice(0, 8)}${timestamp}`;
    }
    // Keep within character limits
    const suffixStr = suffix.toString();
    name = sanitized.slice(0, 12 - suffixStr.length) + suffixStr;
    suffix++;
  }

  return name;
}

export function createDeviceHash(userAgent: string, timestamp: number): string {
  // Simple device fingerprinting - in production, consider more sophisticated methods
  const data = `${userAgent}-${timestamp}`
  return btoa(data).slice(0, 32)
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount / 100) // Stripe amounts are in cents
}

export function formatDate(date: Date | string): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date))
}

export function formatDateTime(date: Date | string): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date))
}

/**
 * Get the local IP address of the machine
 * Used for development to make URLs accessible from other devices on the network
 */
export function getLocalIpAddress(): string | null {
  try {
    const networkInterfaces = os.networkInterfaces();

    for (const interfaceName of Object.keys(networkInterfaces)) {
      const interfaces = networkInterfaces[interfaceName];
      if (!interfaces) continue;
      for (const iface of interfaces) {
        // Skip internal (loopback) and non-IPv4 addresses
        if (iface.family === 'IPv4' && !iface.internal) {
          return iface.address;
        }
      }
    }
  } catch {
    // Using console.warn to avoid circular dependency with logger
    console.warn("Failed to get local IP address");
  }
  return null;
}

/**
 * Get the base URL for the application
 * In development, uses local IP address for easier testing on other devices
 * In production, uses configured environment variables
 */
export function getAppBaseUrl(): string {
  // In production or if explicitly set, use the configured URL
  if (process.env.NODE_ENV === 'production') {
    return process.env.NEXT_PUBLIC_APP_URL || process.env.NEXTAUTH_URL || 'https://yourdomain.com';
  }

  // In development, try to use local IP for easier mobile/device testing
  const localIp = getLocalIpAddress();
  const port = process.env.PORT || '3000';

  if (localIp) {
    return `http://${localIp}:${port}`;
  }

  // Fallback to localhost if we can't get the IP
  return process.env.NEXT_PUBLIC_APP_URL || process.env.NEXTAUTH_URL || `http://localhost:${port}`;
}
